import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TuiAlertService, TuiButton, TuiLoader, TuiTextfield } from '@taiga-ui/core';
import { TuiInputModule, TuiTextfieldControllerModule } from '@taiga-ui/legacy';
import { TuiSearch } from '@taiga-ui/layout';
import { ReportsService } from '../../../core/services/reports.service';
import { ClientArchivedBadgeComponent } from '../../../shared/components/client-archived-badge/client-archived-badge.component';
import { reportArchivedStatus } from '../report-export.util';
import * as XLSX from 'xlsx';

interface RegistrationRenewalRecord {
  number: number;
  date: Date;
  registrationNo: string;
  renewalNo: string;
  clientId?: string;
  newClientName?: string;
  newClientAddress?: string;
  newClientContact?: string;
  renewalClientName?: string;
  renewalClientAddress?: string;
  renewalClientContact?: string;
  annualFee?: number;
  lifeTimeFee?: number;
  renewalDate?: Date | null;
  renewalFee?: number | null;
  periodStart?: Date | null;
  periodEnd?: Date | null;
  clientArchived?: boolean;
}

@Component({
  selector: 'app-registration-renewal-income',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TuiTextfield,
    TuiButton,
    TuiLoader,
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiSearch,
    ClientArchivedBadgeComponent,
  ],
  templateUrl: './registration-renewal-income.component.html',
  styleUrl: './registration-renewal-income.component.scss',
  providers: [DatePipe],
})
export class RegistrationRenewalIncomeComponent implements OnInit {
  private reportsService = inject(ReportsService);
  private alerts = inject(TuiAlertService);
  private datePipe = inject(DatePipe);

  reportData: RegistrationRenewalRecord[] = [];
  loading = false;

  // Filter Type
  reportTypes: string[] = ['Monthly', 'Annually'];
  reportTypeControl = new FormControl<string>('Monthly');

  // Filters
  monthControl = new FormControl<number>(new Date().getMonth());
  yearControl = new FormControl<number>(new Date().getFullYear());

  // Month options
  months = [
    { value: 0, label: 'January' },
    { value: 1, label: 'February' },
    { value: 2, label: 'March' },
    { value: 3, label: 'April' },
    { value: 4, label: 'May' },
    { value: 5, label: 'June' },
    { value: 6, label: 'July' },
    { value: 7, label: 'August' },
    { value: 8, label: 'September' },
    { value: 9, label: 'October' },
    { value: 10, label: 'November' },
    { value: 11, label: 'December' },
  ];

  // Year options (last 5 years to next 2 years)
  years: number[] = [];

  // Totals
  totalAnnualFee = 0;
  totalLifeTimeFee = 0;
  grandTotal = 0;

  defaultLoader(){
this.generateYears();
    const today = new Date();
    this.monthControl.setValue(today.getMonth());
    this.yearControl.setValue(today.getFullYear());
    this.loadReportData();
  }

  ngOnInit(): void {
   this.defaultLoader()
  }

  generateYears(): void {
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 5; i <= currentYear + 2; i++) {
      this.years.push(i);
    }
  }

  loadReportData(): void {
    this.loading = true;
    const reportType = this.reportTypeControl.value;
    const month = this.monthControl.value;
    const year = this.yearControl.value;

    const yearNum = Number(year);
    const monthIndex =
      month === null || month === undefined ? null : Number(month);

    if (reportType === 'Monthly' && (monthIndex === null || Number.isNaN(monthIndex) || year == null)) {
      this.loading = false;
      this.alerts.open('Please select month and year', { appearance: 'error' }).subscribe();
      return;
    }
    if (reportType === 'Annually' && (year == null || Number.isNaN(yearNum))) {
      this.loading = false;
      this.alerts.open('Please select year', { appearance: 'error' }).subscribe();
      return;
    }

    const payload: any = {
      reportType: reportType || 'Monthly',
      year: yearNum,
    };

    if (reportType === 'Monthly') {
      // monthControl is 0–11; backend expects 1–12 (must use Number — select values are strings)
      payload.month = monthIndex! + 1;
    }

    this.reportsService.getRegistrationRenewalIncomeReport(payload).subscribe({
      next: (response: any) => {
        if (response.success && response.data) {
          // Map dates from strings to Date objects
          this.reportData = response.data.reportData.map((record: any) => ({
            ...record,
            date: new Date(record.date),
            periodStart: record.periodStart
              ? new Date(record.periodStart)
              : null,
            periodEnd: record.periodEnd ? new Date(record.periodEnd) : null,
            renewalDate: record.renewalDate
              ? new Date(record.renewalDate)
              : null,
          }));

          // Set totals from backend
          this.totalAnnualFee = response.data.totals.totalAnnualFee || 0;
          this.totalLifeTimeFee = response.data.totals.totalLifeTimeFee || 0;
          // Ensure grandTotal represents the combined total of annual + lifetime fees
          this.grandTotal = (this.totalAnnualFee || 0) + (this.totalLifeTimeFee || 0);
        } else {
          this.reportData = [];
          this.totalAnnualFee = 0;
          this.totalLifeTimeFee = 0;
          this.grandTotal = 0;
        }
        this.loading = false;
      },
      error: (error: any) => {
        console.error('Error loading report:', error);
        this.loading = false;
        this.alerts.open('Error loading report data', { appearance: 'error' }).subscribe();
      },
    });
  }

  applyFilters(): void {
    this.loadReportData();
  }

  clearFilters(): void {
    this.reportTypeControl.setValue('Monthly');
    const today = new Date();
    this.monthControl.setValue(today.getMonth());
    this.yearControl.setValue(today.getFullYear());
    this.reportData = [];
    this.totalAnnualFee = 0;
    this.totalLifeTimeFee = 0;
    this.grandTotal = 0;
  }

  exportToExcel(): void {
    const reportType = this.reportTypeControl.value;
    const month = this.monthControl.value;
    const year = this.yearControl.value;

    let dateRangeText = '';
    if (reportType === 'Monthly' && month !== null && year !== null) {
      const monthIdx = Number(month);
      dateRangeText = `${this.months[monthIdx]?.label ?? ''} ${year}`;
    } else if (reportType === 'Annually' && year !== null) {
      dateRangeText = `Year ${year}`;
    }

    const generated = this.datePipe.transform(new Date(), 'MM/dd/yyyy HH:mm:ss');

    // Prepare data rows
    const data: any[] = [];

    this.reportData.forEach((record) => {
      data.push({
        NUMBER: record.number,
        DATE: this.datePipe.transform(record.date, 'MM.dd.yyyy'),
        'VALID UNTIL': record.periodEnd
          ? this.datePipe.transform(record.periodEnd, 'MM.dd.yyyy')
          : '',
        'REGISTRATION NO. / RENEWAL NO.': record.registrationNo || record.renewalNo,
        STATUS: reportArchivedStatus(record.clientArchived),
        'NEW REGISTERED CLIENT\'S DETAILS': record.newClientName
          ? `${record.newClientName}, ${record.newClientAddress}, ${record.newClientContact}`
          : '',
        'RENEWAL CLIENT\'S DETAILS': record.renewalClientName
          ? `${record.renewalClientName}, ${record.renewalClientAddress}, ${record.renewalClientContact}`
          : '',
        'ANNUAL FEE': record.annualFee !== undefined ? record.annualFee.toFixed(2) : '',
        'LIFE TIME FEE': record.lifeTimeFee !== undefined ? record.lifeTimeFee.toFixed(2) : '',
      });
    });

    // Add Total row (previously labeled Grand Total) showing separate columns
    data.push({
      NUMBER: '',
      DATE: '',
      'VALID UNTIL': '',
      'REGISTRATION NO. / RENEWAL NO.': '',
      STATUS: '',
      'NEW REGISTERED CLIENT\'S DETAILS': '',
      'RENEWAL CLIENT\'S DETAILS': 'TOTAL',
      'ANNUAL FEE': this.totalAnnualFee.toFixed(2),
      'LIFE TIME FEE': this.totalLifeTimeFee.toFixed(2),
    });

    // Add Grand Total row (combined Annual + Lifetime into one value)
    data.push({
      NUMBER: '',
      DATE: '',
      'VALID UNTIL': '',
      'REGISTRATION NO. / RENEWAL NO.': '',
      STATUS: '',
      'NEW REGISTERED CLIENT\'S DETAILS': '',
      'RENEWAL CLIENT\'S DETAILS': 'GRAND TOTAL',
      'ANNUAL FEE': (this.totalAnnualFee + this.totalLifeTimeFee).toFixed(2),
      'LIFE TIME FEE': '',
    });

    // Create empty worksheet
    const worksheet = XLSX.utils.aoa_to_sheet([]);
    XLSX.utils.sheet_add_json(worksheet, data, { origin: 'A6' });

    // Set column widths
    worksheet['!cols'] = [
      { wch: 10 },
      { wch: 15 },
      { wch: 15 },
      { wch: 25 },
      { wch: 12 },
      { wch: 40 },
      { wch: 40 },
      { wch: 15 },
      { wch: 15 },
    ];

    // Add header rows (rows 1-4)
    const headerRows = [
      ['ROYAL HOSPITAL - HOME NURSING CARE'],
      ['Registration & Renewal Income Report'],
      [`Date Range: ${dateRangeText}`],
      [`Generated: ${generated}`],
    ];
    XLSX.utils.sheet_add_aoa(worksheet, headerRows, { origin: 'A1' });

    // Merge cells for headers
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 8 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 8 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 8 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: 8 } },
    ];

    // Style helper function
    const setCellStyle = (cell: string, bold: boolean, size: number) => {
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold, sz: size },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
      }
    };

    // Apply styles to header rows
    setCellStyle('A1', true, 16);
    setCellStyle('A2', true, 14);
    setCellStyle('A3', true, 12);
    setCellStyle('A4', true, 12);

    // Create workbook and write file
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Registration & Renewal');
    XLSX.writeFile(workbook, `Registration_Renewal_Income_${reportType}_${Date.now()}.xlsx`);
  }
}
